import type { Agent } from '@fingerprint/agent'
import { FINGERPRINT_PUBLIC_API_KEY, isDev, isLocalhost, isProd } from 'v2/configs/constants'
import { isAutomatedSession } from 'v2/utils/automation'
import { logger } from 'v2/utils/logger'

// Fingerprint identification at signup: one billed event per signup, never on page load.
// The result is a label only; any failure just leaves the fields out.

// Caps the whole wait on submit, agent download included (the agent's own timeout
// starts only after the script has loaded).
export const SIGNUP_IDENTIFY_TIMEOUT_MS = 4000

/** Fingerprint's own names, so the ids paste straight into its dashboard and Server API. */
export interface SignupDevice {
  fingerprint_visitor_id: string
  fingerprint_event_id: string
}

// linkedId = the email when known, so Fingerprint's dashboard shows device ↔ email.
// No visitor_id means the browser could not be identified.
export const getSignupDevice = async (
  agent: Agent,
  email?: string,
): Promise<SignupDevice | undefined> => {
  const linkedId = email?.trim().toLowerCase()
  const result = await agent.get({
    ...(linkedId && { linkedId }),
    tags: { source: 'cx-console', action: 'signup' },
  })
  if (!result.visitor_id) return undefined
  return {
    fingerprint_visitor_id: result.visitor_id,
    fingerprint_event_id: result.event_id,
  }
}

let agentPromise: Promise<Agent> | null = null

// Deployed builds and real browsers only (same gate as PostHog, plus the automation check).
// Lazy import: the package reaches only the signup chunk, and only past the gate.
export const startFingerprint = () => {
  const apiKey = FINGERPRINT_PUBLIC_API_KEY
  if (agentPromise || !apiKey) return
  if ((!isProd && !isDev) || isLocalhost || isAutomatedSession()) return
  agentPromise = import('@fingerprint/agent').then(({ start }) => start({ apiKey, region: 'us' }))
  // Observed so a preload failure before any submit is not an unhandled rejection.
  agentPromise.catch(() => {})
}

// Transient failures retry on the next submit and stay out of Sentry; any other code is a
// configuration problem worth an alert. A load failure also drops the agent so it reloads.
const RELOAD_CODES = new Set([
  'script_load_fail',
  'csp_block',
  'network_connection',
  'network_abort',
  'client_timeout',
])
// The agent loaded fine and Fingerprint's API had a transient failure: keep it, retry the get.
const RETRY_CODES = new Set([
  'server_error',
  'too_many_requests',
  'failed',
  'request_read_timeout',
  'state_not_ready',
  'bad_response_format',
])

// A rejected import() (the chunk purged by a newer deploy) carries no code and reloads too.
export const recoveryFor = (code?: string): 'reload' | 'retry' | undefined => {
  if (!code || RELOAD_CODES.has(code)) return 'reload'
  return RETRY_CODES.has(code) ? 'retry' : undefined
}

interface Attempt {
  result: Promise<SignupDevice | undefined>
  timedOut: boolean
}
// Keyed by email so editing the address and coming back reuses the earlier result.
const attempts = new Map<string, Attempt>()
const TIMED_OUT = Symbol('timed out')

// One identification per email, shared by every caller, so a resubmit never bills a
// second event. The wait is bounded; after a timeout the same email answers at once.
export const identify = (
  pending: Promise<Agent>,
  email: string,
  timeoutMs = SIGNUP_IDENTIFY_TIMEOUT_MS,
): Promise<SignupDevice | undefined> => {
  let current = attempts.get(email)
  if (!current) {
    const started: Attempt = { timedOut: false, result: Promise.resolve(undefined) }
    started.result = pending
      .then((agent) => getSignupDevice(agent, email))
      .catch((error: { code?: string } | undefined) => {
        const recovery = recoveryFor(error?.code)
        if (recovery) {
          logger.warn('Fingerprint unavailable', error?.code)
          if (recovery === 'reload' && agentPromise === pending) agentPromise = null
          if (attempts.get(email) === started) attempts.delete(email)
        } else {
          logger.error('Fingerprint identification failed', error)
        }
        return undefined
      })
    attempts.set(email, started)
    current = started
  }
  const deadline = new Promise<typeof TIMED_OUT>((resolve) =>
    setTimeout(() => resolve(TIMED_OUT), current.timedOut ? 0 : timeoutMs),
  )
  return Promise.race([current.result, deadline]).then((outcome) => {
    if (outcome !== TIMED_OUT) return outcome
    if (!current.timedOut) logger.warn('Fingerprint identification timed out')
    current.timedOut = true
    return undefined
  })
}

export const identifySignupDevice = (email = ''): Promise<SignupDevice | undefined> => {
  startFingerprint()
  return agentPromise
    ? identify(agentPromise, email.trim().toLowerCase())
    : Promise.resolve(undefined)
}
